import type { FunctionComponent, SetStateAction } from 'react';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAuth } from 'react-oidc-context';

import { VerificationCompleteStatus } from '@endeavour/verification-integration';

import { config } from '../../../../../config/config';
import type {
    IDocument,
    IUserProfilesEnumsVerificationStatus,
} from '../../../../account/queries/verification.graphql';
import { FormattedMessage } from '../../../../shared/components/formatted-message/formatted-message';
import { Gap } from '../../../../shared/components/gap/gap';
import { useToast } from '../../../../shared/hooks/use-toast';
import { Document } from '../../../components/document/document';
import { FileSelector } from '../../../components/file-selector/file-selector';
import { SectionTitleStatus } from '../../../components/section-title-status/section-title-status';
import { VerificationDocumentsButton } from '../../../components/verification-documents-button/verification-documents-button';
import { useVerificationProviderProcessCompleteMutation } from '../../../graphql/verification-provider.graphql';
import type { DocumentSide } from '../../../reducers/user-verification.reducer';
import { UploadIntegration } from '../../upload-integration/upload-integration';

export interface IFileData {
    file: FileList | undefined;
    documentNumber: string;
    expiration: string;
}

export interface IVerificationFormData {
    files: Record<'FRONT' | 'BACK', IFileData>;
}

export interface IDocumentUploadSectionProps {
    title: string | JSX.Element;
    kycType: string;
    verificationStatus: IUserProfilesEnumsVerificationStatus;
    documentTypes: string[];
    userUploadedDocuments: IDocument[];
    reload: () => void;
}

export const DocumentUploadSectionContainer: FunctionComponent<IDocumentUploadSectionProps> = ({
    documentTypes,
    title,
    verificationStatus,
    kycType,
    userUploadedDocuments,
    reload,
}) => {
    const auth = useAuth();
    const userProfileId = auth?.user?.profile.user_profile_id as string;
    const kycConfigSettings = config.KYC.SECTIONS;
    const kycDocumentsSettings = config.KYC.DOCUMENTS;
    const [selectedDocumentType, setSelectedDocumentType] = useState<string>(documentTypes[0]);
    const toast = useToast();
    const intl = useIntl();

    const [addMoreDocuments, setAddMoreDocuments] = useState(false);
    const [startIntegrationProcess, setStartIntegrationProcess] = useState(false);

    const [_, execute] = useVerificationProviderProcessCompleteMutation();

    const submitProcessComplete = useCallback(
        async (data: unknown) => {
            if (!config.TENANT_ID || !userProfileId) {
                return;
            }

            execute({
                xTenantID: config.TENANT_ID,
                userProfileId: userProfileId,
                provider: 'onfido',
                responseInfo: data,
            });
        },
        [execute, userProfileId],
    );

    const onDocumentTypeChange = useCallback((item: React.ChangeEvent<Element>) => {
        const itemTaraget = item.target as HTMLSelectElement;
        const value = itemTaraget.value;
        setSelectedDocumentType(value);
    }, []);

    const onAddMoreDocuments = useCallback((value: SetStateAction<boolean>) => {
        setAddMoreDocuments(value);
    }, []);

    const onDocumentUploadComplete = useCallback(() => {
        reload();
    }, [reload]);

    const onDocumentUploadError = useCallback(() => {
        toast.error(
            intl.formatMessage({
                id: 'kyc-verification.document-upload.error',
                defaultMessage:
                    'An error occurred when uploading your documents. Please check that you are uploading a file below 10MB and it is a PDF, PNG or JPEG file.',
            }),
        );
    }, [toast, intl]);

    const showIntegrationProcess = useCallback(
        () => setStartIntegrationProcess(true),
        [setStartIntegrationProcess],
    );
    const showDocumentUploads = useCallback(() => setAddMoreDocuments(true), [setAddMoreDocuments]);

    const onDocumentUploaded = useCallback(
        (status: VerificationCompleteStatus, data: unknown) => {
            if (status === VerificationCompleteStatus.COMPLETE_SUCCESS) {
                submitProcessComplete(data);
            }
            setStartIntegrationProcess(false);
        },
        [submitProcessComplete],
    );

    const typeSettings = kycConfigSettings.find(({ type }) => {
        return type === kycType;
    });

    const provider = typeSettings?.['provider'];

    const hasDocuments = userUploadedDocuments.filter(({ type }) =>
        documentTypes.includes(type ? type : ''),
    );

    const kycTypeConfig =
        kycDocumentsSettings[selectedDocumentType as keyof typeof kycDocumentsSettings];
    const kycTypeFieldAmounts = kycTypeConfig.SIDES as DocumentSide[];

    return (
        <div>
            <SectionTitleStatus title={title} verificationStatus={verificationStatus} />

            <Gap>
                {hasDocuments?.map((item) => {
                    return <Document key={item?.id} document={item} />;
                })}
            </Gap>

            {provider?.name === 'endeavour' ? (
                <>
                    {!hasDocuments.length || addMoreDocuments ? (
                        <FileSelector
                            hasDocuments={hasDocuments}
                            onAddMoreDocuments={onAddMoreDocuments}
                            documentTypes={documentTypes}
                            selectedDocumentType={selectedDocumentType}
                            kycTypeFieldAmounts={kycTypeFieldAmounts}
                            isDocumentNumberRequired={kycTypeConfig.DOC_NUMBER_REQUIRED}
                            isDocumentExpirationDateRequired={kycTypeConfig.DOC_EXPIRATION_REQUIRED}
                            onDocumentTypeChange={onDocumentTypeChange}
                            onDocumentUploadComplete={onDocumentUploadComplete}
                            onDocumentUploadError={onDocumentUploadError}
                        />
                    ) : null}
                    {hasDocuments.length && !addMoreDocuments ? (
                        <VerificationDocumentsButton
                            hollow
                            buttonText={
                                <FormattedMessage
                                    id="kyc-verification.add-more-documents"
                                    defaultMessage="Add more documents"
                                />
                            }
                            onClick={showDocumentUploads}
                        />
                    ) : null}
                </>
            ) : (
                <>
                    {!hasDocuments.length && !startIntegrationProcess ? (
                        <VerificationDocumentsButton
                            buttonText={
                                <FormattedMessage
                                    id="kyc-verification.start-verification-process"
                                    defaultMessage="Start Verification Process"
                                />
                            }
                            onClick={showIntegrationProcess}
                        />
                    ) : (
                        <VerificationDocumentsButton
                            hollow
                            buttonText={
                                <FormattedMessage
                                    id="kyc-verification.recomplete-verification-process"
                                    defaultMessage="Recomplete verification process"
                                />
                            }
                            onClick={showIntegrationProcess}
                        />
                    )}

                    {startIntegrationProcess ? (
                        <UploadIntegration
                            onDocumentUploaded={onDocumentUploaded}
                            providerConfig={provider?.config}
                        />
                    ) : null}
                </>
            )}
        </div>
    );
};
